import {Action, Selector, State, StateContext} from '@ngxs/store';
import {CalorieTrackingNamespace} from './calorie-tracking.namespace';
import {MacronutrientDistributionNamespace} from './macronutrient-distribution.namespace';
import {NutrientTableNamespace} from './nutrient-table.namespace';
import {Injectable} from '@angular/core';

export interface IRMealPlanBuilder {
  calorieTracking: Partial<CalorieTrackingNamespace.ICalorieTracking> | null;
  macronutrientDistribution: Partial<MacronutrientDistributionNamespace.IMacronutrientDistribution> | null;



  periodId: number;
  nutrientTable: Partial<NutrientTableNamespace.INutrientTable> | null;
  // para obtener como key la categoría id con un objeto con su nombre
  nutrientTableWithName: Partial<NutrientTableNamespace.INutrientTableWithName> | null;
  
  
  
  // la sumatoria de macronutrientes en la tabla SMAE
  smaeTable: NutrientTableNamespace.ISmaeTable;
  // para indicar cuando los bloques de planes van a mostrar alertas por alimentos alérgicos
  canShowAllergicMode?: boolean;

}

export namespace MealPlanBuilderAction {
  export class PatchCalorieTracking {
    static readonly type: string = '[Meal Plan Builder] Patch Calorie Tracking Action';
    constructor(public calorieTracking: Partial<CalorieTrackingNamespace.ICalorieTracking> | null) { }
  }

  export class PatchMacronutrientDistribution {
    static readonly type: string = '[Meal Plan Builder] Patch Macronutrient Distribution Action';
    constructor(public macronutrientDistribution: Partial<MacronutrientDistributionNamespace.IMacronutrientDistribution> | null) { }
  }

  export class PatchNutrientTable {
    static readonly type: string = '[Meal Plan Builder] Patch Nutrient Table Action';
    constructor(public nutrientTable: Partial<NutrientTableNamespace.INutrientTable> | null) { }
  }

  export class PatchNutrientTableWithName {
    static readonly type: string = '[Meal Plan Builder] Patch Nutrient Table With Name Action';
    constructor(public nutrientTableWithName: Partial<NutrientTableNamespace.INutrientTableWithName> | null) { }
  }

  export class PatchMacronutrientsSmaeTable {
    static readonly type: string = '[Meal Plan Builder] Patch Macronutrient Smae Table Action';
    constructor(public smaeTable: NutrientTableNamespace.ISmaeTable | null) { }
  }

  export class ResetState {
    static readonly type: string = '[Meal Plan Builder] Reset State Action';
    constructor() { }
  }

  export class SetCanShowAllergicMode {
    static readonly type: string = '[Meal Plan Builder] Set Can Show Allergic Mode Action';
    constructor(public value: boolean) { }
  }
}

const DEFAULT_MEAL_PLAN_BUILDER: IRMealPlanBuilder = {
  calorieTracking: null,
  macronutrientDistribution: null,
  nutrientTable: null,
  nutrientTableWithName: null,
  smaeTable: null,
  periodId: null
};

@State<IRMealPlanBuilder>({
  name: 'MealPlanBuilderState',
  defaults: DEFAULT_MEAL_PLAN_BUILDER,
})
@Injectable()
export class MealPlanBuilderState {
  constructor() { }

  @Selector()
  static getCalorieTracking(
    {calorieTracking}: IRMealPlanBuilder
  ): Partial<CalorieTrackingNamespace.ICalorieTracking> | null {
    return calorieTracking;
  }

  @Selector()
  static getMacronutrientDistribution(
    {macronutrientDistribution}: IRMealPlanBuilder
  ): Partial<MacronutrientDistributionNamespace.IMacronutrientDistribution> | null {
    return macronutrientDistribution;
  }

  @Selector()
  static getSmaeGrams(
    {macronutrientDistribution}: IRMealPlanBuilder
  ): Partial<MacronutrientDistributionNamespace.IMacronutrientSmaeGrams> | null {
    return macronutrientDistribution?.smaeGrams;
  }

  @Selector()
  static getNutrientTable(
    {nutrientTable}: IRMealPlanBuilder
  ): Partial<NutrientTableNamespace.INutrientTable> | null {
    return nutrientTable;
  }

  @Selector()
  static getNutrientTableWithName(
    {nutrientTableWithName}: IRMealPlanBuilder
  ): Partial<NutrientTableNamespace.INutrientTableWithName> | null {
    return nutrientTableWithName;
  }

  @Selector()
  static getMacronutrientsSmaeTable(
    {smaeTable}: IRMealPlanBuilder
  ): NutrientTableNamespace.ISmaeTable | null {
    return smaeTable;
  }

  @Selector()
  static getCanShowAllergicMode(
    {canShowAllergicMode}: IRMealPlanBuilder
  ): boolean {
    return canShowAllergicMode;
  }

  @Action(MealPlanBuilderAction.PatchCalorieTracking)
  async patchCalorieTracking(
    {patchState, getState}: StateContext<IRMealPlanBuilder>,
    {calorieTracking}: MealPlanBuilderAction.PatchCalorieTracking
  ): Promise<void> {
    patchState({
      calorieTracking: {...(getState().calorieTracking ?? {}), ...calorieTracking},
    });
  }

  @Action(MealPlanBuilderAction.PatchMacronutrientDistribution)
  async patchMacronutrientDistribution(
    {patchState, getState}: StateContext<IRMealPlanBuilder>,
    {macronutrientDistribution}: MealPlanBuilderAction.PatchMacronutrientDistribution
  ): Promise<void> {
    patchState({
      macronutrientDistribution: {...(getState().macronutrientDistribution ?? {}), ...macronutrientDistribution},
    });
  }

  @Action(MealPlanBuilderAction.PatchNutrientTable)
  async patchNutrientTable(
    {patchState, getState}: StateContext<IRMealPlanBuilder>,
    {nutrientTable}: MealPlanBuilderAction.PatchNutrientTable
  ): Promise<void> {
    patchState({
      nutrientTable: {...(getState().nutrientTable ?? {}), ...nutrientTable},
    });
  }

  @Action(MealPlanBuilderAction.PatchNutrientTableWithName)
  async patchNutrientTableWithName(
    {patchState, getState}: StateContext<IRMealPlanBuilder>,
    {nutrientTableWithName}: MealPlanBuilderAction.PatchNutrientTableWithName
  ): Promise<void> {
    patchState({
      nutrientTableWithName: {...(getState().nutrientTableWithName ?? {}), ...nutrientTableWithName},
    });
  }

  @Action(MealPlanBuilderAction.PatchMacronutrientsSmaeTable)
  async patchMacronutrientsSmaeTable(
    {patchState}: StateContext<IRMealPlanBuilder>,
    {smaeTable}: MealPlanBuilderAction.PatchMacronutrientsSmaeTable
  ): Promise<void> {
    patchState({
      smaeTable: {...smaeTable},
    });
  }

  @Action(MealPlanBuilderAction.ResetState)
  async resetState({setState}: StateContext<IRMealPlanBuilder>, {}: MealPlanBuilderAction.ResetState): Promise<void> {
    setState(DEFAULT_MEAL_PLAN_BUILDER);
  }

  @Action(MealPlanBuilderAction.SetCanShowAllergicMode)
  async setCanShowAllergicMode({patchState}: StateContext<IRMealPlanBuilder>, {value}: MealPlanBuilderAction.SetCanShowAllergicMode): Promise<void> {
    patchState({
      canShowAllergicMode: value
    });
  }
}
